import "./register.css";
import { Button, Form } from "react-bootstrap";
import { Logo } from "../logo/logo";
import { ArrowLeft, Mail } from "../icons";
import { Container, ErrorText, InnerContainer } from "../commons";

import { useFormik } from "formik";
import axios, { AxiosError } from "axios";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { createT } from "../../i18n/strings";

const apiUrl =
  process.env.REACT_APP_ENV === "LOCAL"
    ? "http://localhost:5000"
    : "https://api.spotprisprognos.se:8443";

const Register = (props: any) => {
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState("");
  const navigate = useNavigate();
  // Auth pages are intentionally Swedish-only (see createT) so signup never
  // flips between languages mid-flow regardless of the stored UI language.
  const t = useMemo(() => createT("sv"), []);

  // Force light theme on register page only
  useEffect(() => {
    const prev = document.documentElement.getAttribute("data-bs-theme");
    document.documentElement.setAttribute("data-bs-theme", "light");
    return () => {
      if (prev) document.documentElement.setAttribute("data-bs-theme", prev);
    };
  }, []);

  const onSubmit = async (values: any) => {
    setError("");
    setSuccess(false);

    // Check if passwords match
    if (values.password !== values.confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    try {
      const response = await axios.post(`${apiUrl}/api/accounts`, {
        username: values.username,
        email: values.email,
        password: values.password,
      });

      // Show success message instead of auto-login
      setSuccess(true);
      setRegisteredEmail(values.email);
    } catch (err) {
      if (err && err instanceof AxiosError) {
        const errorCode = err.response?.data?.error;
        if (errorCode === "EmailAlreadyExists") {
          setError(t("register.emailExists"));
        } else {
          setError(
            err.response?.data?.message ||
              errorCode ||
              t("register.genericError"),
          );
        }
      } else if (err && err instanceof Error) setError(err.message);

      console.log("Error: ", err);
    }
  };

  const formik = useFormik({
    initialValues: {
      username: "",
      email: "",
      password: "",
      confirmPassword: "",
    },
    onSubmit,
  });

  // Show success message after registration
  if (success) {
    return (
      <Container className="register-container">
        <span className="auth-blob b1" aria-hidden="true" />
        <span className="auth-blob b2" aria-hidden="true" />
        <span className="auth-blob b3" aria-hidden="true" />
        <a href="https://spotprisprognos.se" className="auth-back">
          <ArrowLeft style={{ width: "0.9em", height: "0.9em", verticalAlign: "-0.12em" }} /> Tillbaka
        </a>
        <div className="banner-text">
          <h1>{t("register.banner")}</h1>
        </div>
        <InnerContainer className="register-inner-container">
          <div className="logo-section">
            <Logo size="medium" showCredit />
          </div>
          <div className="register-success">
            <div className="mail-icon" aria-hidden="true">
              <Mail size="1.6em" />
            </div>
            <h2>{t("register.checkEmailTitle")}</h2>
            <p className="message">
              {t("register.successMessagePrefix")}{" "}
              <span className="email">{registeredEmail}</span>
              {t("register.successMessageSuffix")}
            </p>
            <Button variant="primary" onClick={() => navigate("/login")}>
              {t("register.goToLogin")}
            </Button>
          </div>
        </InnerContainer>
      </Container>
    );
  }

  return (
    <Container className="register-container">
      <span className="auth-blob b1" aria-hidden="true" />
        <span className="auth-blob b2" aria-hidden="true" />
        <span className="auth-blob b3" aria-hidden="true" />
        <a href="https://spotprisprognos.se" className="auth-back">
          <ArrowLeft style={{ width: "0.9em", height: "0.9em", verticalAlign: "-0.12em" }} /> Tillbaka
        </a>
      <div className="banner-text">
        <h1>{t("register.banner")}</h1>
      </div>
      <InnerContainer className="register-inner-container">
        <div className="logo-section">
          <Logo size="medium" showCredit />
        </div>
        <Form onSubmit={formik.handleSubmit}>
          <h1>{t("register.welcome")}</h1>
          <ErrorText>{error}</ErrorText>

          <Form.Group className="mb-3">
            <Form.Control
              name="username"
              value={formik.values.username}
              onChange={formik.handleChange}
              placeholder={t("register.username")}
              type="text"
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Control
              name="email"
              value={formik.values.email}
              onChange={formik.handleChange}
              placeholder={t("register.email")}
              type="email"
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Control
              name="password"
              value={formik.values.password}
              onChange={formik.handleChange}
              placeholder={t("register.password")}
              type="password"
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Control
              name="confirmPassword"
              value={formik.values.confirmPassword}
              onChange={formik.handleChange}
              placeholder={t("register.confirmPassword")}
              type="password"
            />
          </Form.Group>

          <div className="mt-4">
            <Button
              variant="primary"
              type="submit"
              disabled={formik.isSubmitting}
            >
              {t("register.button")}
            </Button>
          </div>

          <div className="register-link">
            {t("register.hasAccount")}
            <a href="/login">{t("register.loginLink")}</a>
          </div>

          <div className="register-terms">
            {t("register.privacyPrefix")}{" "}
            <a
              href="https://spotprisprognos.se/integritetspolicy"
              target="_blank"
              rel="noopener noreferrer"
            >
              {t("register.privacyLink")}
            </a>
            .
          </div>
        </Form>
      </InnerContainer>
    </Container>
  );
};

export { Register };
